import React, { Suspense, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

/**
 * DWEB-193 §1: mount a lazy section only when it comes NEAR the viewport.
 *
 * `React.lazy` defers a chunk until the component RENDERS, and on `/` every
 * section renders at once — so the lazy boundary bought nothing on the page
 * that needed it. Worse, the homepage route has ONE <Suspense> around every
 * section, Hero included, so the hero <h1> (the LCP element) waited for the
 * slowest lazy chunk. Measured on the LOCAL build at 2945ef8: holding the
 * three.js chunk back by 4 s delayed the React-rendered <h1> from ~300 ms to
 * ~4,200 ms, because AppDemos pulls three.js and suspended that boundary.
 *
 * Until the section is within `rootMargin` of the viewport this renders an
 * empty placeholder that carries the section's id and RESERVES its height, so
 * the document below it does not move when the real section arrives. Once
 * near, the child mounts inside its OWN <Suspense>, whose fallback is the same
 * placeholder, so the route's boundary is never suspended again.
 *
 * DWEB-26 extends it from the apps section to every lazy homepage section
 * (Services, Proof, Contact, FAQ, Partners). Each keeps its own Suspense, so
 * the route's shared boundary no longer wraps anything that can suspend. Any
 * interaction mounts ALL of them at once (each instance listens), so a nav
 * jump is computed against real sections wherever it passes through.
 *
 * `reserve` is { phone, tablet, desktop } in px, the section's measured OUTER
 * height (height + its vertical margins — the placeholder has none) at <=480,
 * 481-991 and >=992 wide. Re-measure it when the section's
 * content changes: a wrong reserve is layout shift below the section when it
 * mounts, and a wrong landing for a nav click that passes through it.
 */
const Reserved = styled.div`
  min-height: ${({ $reserve }) => $reserve.desktop}px;

  @media (max-width: 991px) {
    min-height: ${({ $reserve }) => $reserve.tablet}px;
  }

  @media (max-width: 480px) {
    min-height: ${({ $reserve }) => $reserve.phone}px;
  }
`;

/** Renders nothing; runs `onCommit` before paint once the real section has committed. */
function Committed({ onCommit }) {
  useLayoutEffect(() => {
    onCommit();
  }, [onCommit]);
  return null;
}

/**
 * Any of these mounts the section at once. A smooth scroll computes its target
 * when it starts and then drives absolute positions, so a section that mounts
 * DURING a nav jump moves the destination under it: measured at 375 wide, a
 * jump to Contact landed 357 px down instead of 64, and at 412 wide it
 * overshot to -141. A nav click always begins with a pointerdown (on a phone
 * the hamburger tap comes first), so mounting at the first interaction has the
 * section real before any jump is computed. Lighthouse never interacts, so the
 * first screen it measures is still without the section.
 */
const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];

/**
 * DWEB-26: run `fn` once the page has not scrolled for SCROLL_IDLE_MS — at
 * once, synchronously, when it is already idle. One passive listener for the
 * whole module; it only records a timestamp. It keeps the correction out of a
 * scroll that is visibly MOVING (react-scroll writes an absolute position every
 * frame and would overwrite it). It CANNOT see a native smooth scroll that has
 * been requested but not yet painted its first frame — see `compensate`.
 */
const SCROLL_IDLE_MS = 150;
let lastScrollAt = -Infinity;
if (typeof window !== 'undefined') {
  window.addEventListener('scroll', () => { lastScrollAt = performance.now(); }, { passive: true });
}
function whenScrollIdle(fn) {
  const since = performance.now() - lastScrollAt;
  if (since >= SCROLL_IDLE_MS) {
    fn();
    return;
  }
  setTimeout(() => whenScrollIdle(fn), SCROLL_IDLE_MS - since);
}

export default function MountNearViewport({ id, reserve, rootMargin = '2000px 0px', eager = false, children }) {
  const [near, setNear] = useState(eager);
  const ref = useRef(null);
  const reservedPxRef = useRef(0);

  useEffect(() => {
    if (near) return undefined;
    const el = ref.current;
    if (!el || typeof IntersectionObserver !== 'function') {
      setNear(true);
      return undefined;
    }
    const mount = () => {
      reservedPxRef.current = el.offsetHeight;
      setNear(true);
    };
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) mount();
      },
      { rootMargin },
    );
    observer.observe(el);
    INTERACTION_EVENTS.forEach((e) => window.addEventListener(e, mount, { passive: true }));
    // DWEB-26: a section SCROLLED PAST mounts at once. An instant jump over it
    // (a scrollbar drag, find-in-page, a programmatic scrollTo) never makes it
    // intersect, so the observer never fires, and it would otherwise mount at
    // the NEXT interaction — above the viewport, while that interaction's own
    // smooth scroll is starting, where its correction cancels that scroll
    // (measured; see `compensate`). Mounted now, it has long committed by then.
    const onScroll = () => {
      if (el.getBoundingClientRect().bottom < 0) mount();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      observer.disconnect();
      INTERACTION_EVENTS.forEach((e) => window.removeEventListener(e, mount));
      window.removeEventListener('scroll', onScroll);
    };
  }, [near, rootMargin]);

  /**
   * A MANUAL SCROLL ANCHOR. The reserve cannot be exact at every width — the
   * apps section measured 4,562 px at 375 wide and 4,346 at 390 against one
   * 4,300 phone reserve — so when the section mounts while it starts ABOVE the
   * viewport (a jump past it, a restored scroll position), everything on screen
   * would move by the difference. Measured at 375x812: the contact form's first
   * field moved 441 -> 703 px the instant the section mounted, which is what
   * header-height.spec.js's Test 3 caught. The browser's own scroll anchoring
   * (overflow-anchor: auto) did NOT compensate. So the difference is scrolled
   * away here — before paint when nothing is scrolling.
   *
   * 🔴 DWEB-26: THIS scrollBy CANCELS A NATIVE SMOOTH SCROLL THAT IS PENDING.
   * This comment used to say an in-flight smooth scroll "is not corrected"; it
   * is ABORTED. Measured at 1280x900 on header-height.spec.js Test 3's steps:
   * the submit's pointerdown mounted Proof (jumped over, still a placeholder);
   * antd called scroll({ behavior: 'smooth' }) 50 ms after the click; the main
   * thread stayed busy ~500 ms, so no scroll frame had painted; Proof committed
   * with a 2 px delta and this scrollBy(0, 2) ran — and the smooth scroll never
   * started. The field stayed at -120 px. CONTROL: the same steps with only
   * this call blocked, Proof still committing mid-scroll — antd's scroll ran to
   * the end and the field landed at 313 px. So the commit does not cancel; the
   * correction does. Deferring it to scroll-idle does NOT help (a pending
   * native scroll emits no scroll event). The defence is upstream: a section
   * scrolled past mounts at once (the effect above), so it is not mounting
   * when a later interaction starts a smooth scroll. The residual — a smooth
   * scroll that itself passes a section whose chunk commits mid-flight with a
   * large (phone) delta — is recorded on DWEB-26, not fixed here.
   */
  const compensate = useCallback(() => {
    const el = document.getElementById(id);
    if (!el || !reservedPxRef.current) return;
    if (el.getBoundingClientRect().top >= 0) return;
    // OUTER height against the placeholder's: the section's margin sits outside
    // offsetHeight, and a placeholder has no margin, so a height-only delta
    // missed one margin per section (48 px on desktop) — measured, DWEB-26.
    const cs = getComputedStyle(el);
    const outer = el.offsetHeight + parseFloat(cs.marginTop) + parseFloat(cs.marginBottom);
    const delta = Math.round(outer - reservedPxRef.current);
    if (delta) whenScrollIdle(() => window.scrollBy(0, delta));
  }, [id]);

  const placeholder = (
    <Reserved ref={near ? undefined : ref} id={id} $reserve={reserve} aria-busy="true" data-deferred-section={id} />
  );
  if (!near) return placeholder;
  return (
    <Suspense fallback={placeholder}>
      {children}
      <Committed onCommit={compensate} />
    </Suspense>
  );
}
